// Left-hand progressive checklist sidebar for the Loan File Detail page.
import React, { useEffect, useMemo, useState } from 'react';
import { CircleCheck, Circle, Mail, Phone, MessageSquare } from 'lucide-react';
import { losApi } from '../../losApi';
import PickSelect from '../../shared/PickSelect';
import { useBorrowerInvite } from './useBorrowerInvite';
import { LOAN_PURPOSE_LABELS, labelFor } from '../../shared/labels';
import { SIDEBAR_TOP, SIDEBAR_BOTTOM, SIDEBAR_ARIVE, computeCompletion } from './sidebarCatalog';

export function LoanSidebar({ loan, section, setSection }) {
  const completion = useMemo(() => computeCompletion(loan), [loan]);
  const invite = useBorrowerInvite(loan.loan_file_id);
  // Counts live in other collections; refetch after a save (loan changes) and on each tab switch.
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    let live = true;
    losApi.loanCounts?.(loan.loan_file_id).then((c) => { if (live) setCounts(c); }).catch(() => {});
    return () => { live = false; };
  }, [loan, section]);
  const b = loan.urla?.borrower || {};
  const name = [b.first_name, b.last_name].filter(Boolean).join(' ') || 'Unnamed borrower';
  const addr = loan.property_address || {};
  const purposeLabel = labelFor(LOAN_PURPOSE_LABELS, loan.loan_purpose);

  return (
    <aside className="w-64 shrink-0 hidden lg:block" data-testid="loan-sidebar">
      <div
        className="rounded-2xl overflow-hidden sticky top-[220px] relative"
        style={{
          background: '#ffffff',
          boxShadow: '0 1px 3px rgba(7,11,95,0.06), 0 12px 32px -8px rgba(7,11,95,0.12)',
          border: '1px solid rgba(7,11,95,0.08)',
        }}
      >
        <div className="h-[3px]" style={{ background: 'linear-gradient(90deg, #6BA82E 0%, #8bc94f 60%, #6BA82E 100%)' }} />

        {/* Borrower panel */}
        <div className="p-4 border-b border-slate-100">
          <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#6BA82E' }}>{purposeLabel}</div>
          <div className="font-semibold text-[14px] mt-1 truncate" style={{ color: '#070B5F' }}>{name}</div>
          {invite.canInvite && (
            <div className="flex items-center gap-3 mt-0.5">
              <button type="button" onClick={invite.send} disabled={invite.sending} data-testid="sidebar-invite-borrower"
                className="text-[11px] hover:underline disabled:opacity-50" style={{ color: '#070B5F' }}>
                {invite.sending ? 'Sending…' : 'Invite Borrower'}
              </button>
              <button type="button" onClick={invite.copyLink} data-testid="sidebar-copy-borrower-link"
                className="text-[11px] hover:underline text-slate-500">Copy link</button>
            </div>
          )}
          <div className="flex items-center gap-1.5 mt-2 text-slate-400">
            <button className="w-6 h-6 rounded hover:bg-slate-100 flex items-center justify-center" title="Email"><Mail size={11} /></button>
            <button className="w-6 h-6 rounded hover:bg-slate-100 flex items-center justify-center" title="Phone"><Phone size={11} /></button>
            <button className="w-6 h-6 rounded hover:bg-slate-100 flex items-center justify-center" title="Chat"><MessageSquare size={11} /></button>
          </div>
          <div className="text-[11px] text-slate-500 mt-2 leading-tight">
            {addr.street || '—'}<br />
            {[addr.city, addr.state, addr.zip].filter(Boolean).join(' · ') || ''}
          </div>
          <div className="text-[11px] mt-1 font-semibold" style={{ color: '#030524' }}>
            {(loan.property_type || 'sfr').toUpperCase()}
          </div>
        </div>

        {/* Top sections */}
        <div className="py-2">
          {SIDEBAR_TOP.map((s) => (
            <SidebarRow key={s.key} item={s} active={section === s.key} onClick={() => setSection(s.key)} complete={completion[s.key]} />
          ))}
        </div>

        <div className="border-t border-slate-100 py-2">
          {loan.arive && (
            <SidebarRow item={SIDEBAR_ARIVE} active={section === SIDEBAR_ARIVE.key} onClick={() => setSection(SIDEBAR_ARIVE.key)} />
          )}
          {SIDEBAR_BOTTOM.map((s) => (
            <SidebarRow key={s.key} item={s} active={section === s.key} onClick={() => setSection(s.key)} counts={counts} />
          ))}
        </div>
      </div>
    </aside>
  );
}

function SidebarRow({ item, active, onClick, complete, counts }) {
  const Icon = item.icon;
  let count = null;
  if (counts?.client_needs_total && item.key === 'client_needs') count = `${counts.client_needs_done}/${counts.client_needs_total}`;
  if (counts?.conditions_total && item.key === 'conditions') count = `${counts.conditions_cleared}/${counts.conditions_total}`;
  if (counts && item.key === 'documents') count = counts.documents || null;

  return (
    <button
      onClick={onClick}
      data-testid={`sidebar-${item.key}`}
      className={`w-full flex items-center gap-2 px-4 py-2 text-[13px] transition text-left ${
        active ? 'font-semibold border-l-[3px]' : 'hover:bg-slate-50/50 border-l-[3px] border-transparent'
      }`}
      style={active ? { borderLeftColor: '#6BA82E', color: '#070B5F', background: 'linear-gradient(90deg, rgba(107,168,46,0.08) 0%, transparent 100%)' } : { color: '#334155' }}
    >
      {complete !== undefined ? (
        complete
          ? <CircleCheck size={14} style={{ color: '#6BA82E' }} className="shrink-0" />
          : <Circle size={14} className="text-slate-300 shrink-0" />
      ) : (
        <Icon size={13} className={`shrink-0 ${item.ai ? 'text-purple-500' : 'text-slate-400'}`} />
      )}
      <span className="flex-1 truncate">{item.label}</span>
      {item.dot && <span className="w-1.5 h-1.5 rounded-full bg-red-500" />}
      {item.badge && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: '#F3FAE8', color: '#4c7f21' }}>{item.badge}</span>}
      {count !== null && count !== 0 && <span className="text-[10px] text-slate-400 font-mono">{count}</span>}
    </button>
  );
}

// Below lg the sidebar is hidden, so phones and tablets pick a section here.
export function LoanSectionPicker({ section, setSection, loan }) {
  return (
    <div className="lg:hidden mb-3" data-testid="loan-section-picker">
      <PickSelect
        label="Loan file section"
        labelClassName="sr-only"
        value={section}
        onChange={setSection}
        options={[
          ...SIDEBAR_TOP.map((s) => ({ value: s.key, label: s.label, group: 'Loan file' })),
          ...[...(loan?.arive ? [SIDEBAR_ARIVE] : []), ...SIDEBAR_BOTTOM]
            .map((s) => ({ value: s.key, label: s.label, group: 'Workspace' })),
        ]}
      />
    </div>
  );
}
